import { getAuthStateFromToken } from '../../utils/auth';
import { labelMetabaseCollection, parseMetabaseCollections } from './collections';
import { hasMetabaseAccess } from './planAccess';
import * as MetabaseTypes from './internal/types';
import { client } from '../../apollo';
import { ALL_USER_ASSIGNED_METABASE_DASHBOARDS, METABASE_LOGIN_TOKEN, METABASE_REQUEST } from '../../graphql';
import { ALL_USER_ASSIGNED_METABASE_DASHBOARDSType } from '../../graphql/queries/AllUserAssignedMetabaseDashboards';

/**
 * Metabase client service.
 *
 * IB-1850: only organisations whose plan includes Metabase use any of this (enforced again on the server).
 * IB-1886: the browser no longer creates Metabase users, groups, collections or permissions and never holds
 * a Metabase admin or user session. All of that happens on the ImpactBoard server:
 *  - getLoginToken: the server signs the SSO JWT from the verified ImpactBoard token
 *  - collection reads: proxied by the server (metabaseRequest) as the organisation's Metabase user
 *  - new dashboards: created by the server in the organisation's collection (createMetabaseDashboard)
 */

const METABASE_LOG = `${process.env.NODE_ENV}` === 'development' || process.env.REACT_APP_METABASE_CONSOLE_LOG === '1';
const METABASE_DISABLED =
  process.env.REACT_APP_METABASE_ENABLED === undefined ? true : !process.env.REACT_APP_METABASE_ENABLED;

/** Full-app embedding: hide Metabase branding and navigation (IB-1886) */
export const METABASE_EMBED_MODS: { [key: string]: string } = {
  logo: 'false',
  top_nav: 'false',
  side_nav: 'false',
};

var loggedMetabaseDisabled = false;

export const metabaseService = () => {
  const METABASE_URL = (process.env.REACT_APP_METABASE_URL || 'https://metabase.impactboard.tech').replace(/\/+$/, '');

  const logMetabaseDisabled = () => {
    if (!loggedMetabaseDisabled) {
      console.log('Metabase disabled.');
      loggedMetabaseDisabled = true;
    }
  };

  const isMetabaseDisabled = (): boolean => {
    if (METABASE_DISABLED) logMetabaseDisabled();
    return METABASE_DISABLED;
  };

  const hasOrganisation = (): boolean => {
    const ibToken = getAuthStateFromToken();
    return !!ibToken.user && !!ibToken.user.userOrganisation && !!ibToken.user.userOrganisation.organisation.id;
  };

  /** Organisation is signed in and its plan includes Metabase (IB-1850) */
  const canUseMetabase = (): boolean => hasOrganisation() && hasMetabaseAccess(getAuthStateFromToken().user.plan);

  const getDeploymentEnv = (): 'develop' | 'staging' | 'prod' => {
    const state = (process.env.REACT_APP_DEPLOYMENT_STATE || '').toLowerCase();
    if (state === 'develop' || state === 'staging' || state === 'prod') return state;
    if (state === 'uat') return 'staging';
    const endpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || '';
    if (endpoint === 'https://dev-api.impactboard.tech' || endpoint === 'http://localhost:4000') return 'develop';
    if (endpoint === 'https://uat-api.impactboard.tech') return 'staging';
    return 'prod';
  };

  /** Metabase collection for this environment ("System"); org collections sit directly under it */
  const getEnvironmentCollectionId = (): number => {
    const configured = parseInt(process.env.REACT_APP_METABASE_ENV_COLLECTION_ID || '', 10);
    if (!isNaN(configured)) return configured;
    const env = getDeploymentEnv();
    return env === 'develop' ? 6 : env === 'staging' ? 7 : 8;
  };

  /** Name of the organisation's Metabase collection, as the server provisions it */
  const getOrganisationCollectionName = (): string => {
    const userOrganisation: any = getAuthStateFromToken().user.userOrganisation;
    const organisation = userOrganisation && userOrganisation.organisation;
    if (!organisation || !organisation.name) return '';
    const types: { [key: string]: string } = {
      funder: 'Funder',
      serviceProvider: 'ServiceProvider',
      institution: 'Institution',
    };
    return `${organisation.name} ${types[organisation.type] || ''}`.trim();
  };

  /** GET a whitelisted Metabase API path through the ImpactBoard server */
  const apiGet = async (url: string, params?: { [key: string]: any }): Promise<any> => {
    if (METABASE_LOG) console.log('Metabase API', url, params);
    const result: any = await client.query({
      query: METABASE_REQUEST,
      variables: { input: { path: url, params: params ? JSON.stringify(params) : null } },
      fetchPolicy: 'no-cache',
    });
    const data = result && result.data && result.data.result && result.data.result.data;
    return data ? JSON.parse(data) : null;
  };

  // Collections
  const apiCollectionAll = async (parsed?: boolean): Promise<MetabaseTypes.CollectionAll> => {
    if (isMetabaseDisabled() || !canUseMetabase()) {
      return [];
    }
    var result = ((await apiGet('collection')) || []) as MetabaseTypes.CollectionAll;
    if (!!parsed) {
      result = parseMetabaseCollections(result, getEnvironmentCollectionId(), getOrganisationCollectionName());
    }
    return result;
  };

  const apiCollectionGet = async (collectionId: number, parsed?: boolean): Promise<MetabaseTypes.Collection | null> => {
    if (isMetabaseDisabled() || !canUseMetabase()) {
      return null;
    }
    const result = (await apiGet(`collection/${collectionId}`)) as MetabaseTypes.Collection;
    if (!!result && !!parsed) {
      return labelMetabaseCollection(result, getEnvironmentCollectionId(), getOrganisationCollectionName());
    }
    return result;
  };

  const apiCollectionItemsFilterItem = (
    item: MetabaseTypes.CollectionGetItemsModelType,
    fetchItemsOf: MetabaseTypes.ModelDisplayType[] | undefined
  ): boolean => {
    if (isMetabaseDisabled()) {
      return false;
    }
    if (fetchItemsOf === undefined || fetchItemsOf.length === 0) return true;
    if (fetchItemsOf[0].model === undefined) {
      if (fetchItemsOf[0].display === undefined || fetchItemsOf[0].display.length === 0) return true;
      return fetchItemsOf[0].display.includes(item.display);
    }
    const fetchItem = fetchItemsOf.find(x => x.model === item.model);
    if (!fetchItem) return false;
    if (fetchItem.display === undefined || fetchItem.display.length === 0) return true;
    return fetchItem.display.includes(item.display);
  };

  const apiCollectionItemsFilter = (
    items: MetabaseTypes.CollectionGetItemsModelType[],
    fetchItemsOf: MetabaseTypes.ModelDisplayType[] | undefined
  ): MetabaseTypes.CollectionGetItemsModelType[] => {
    if (isMetabaseDisabled()) {
      return [];
    }
    return items.filter(item => apiCollectionItemsFilterItem(item, fetchItemsOf));
  };

  const apiCollectionGetItems = async (
    collectionId: number,
    options?: {
      models?: MetabaseTypes.ModelType[];
      modelItems?: MetabaseTypes.ModelDisplayType[];
      currentUser?: boolean;
    }
  ): Promise<MetabaseTypes.CollectionGetItemsType> => {
    const empty: MetabaseTypes.CollectionGetItemsType = {
      total: 0,
      data: [],
      models: [],
      limit: null,
      offset: null,
    };
    if (isMetabaseDisabled() || !canUseMetabase()) {
      return empty;
    }
    const models: string[] = [];
    if (!!options) {
      if (!!options.models) models.push(...options.models);
      if (!!options.modelItems && options.modelItems.length > 0 && !!options.modelItems[0].model) {
        options.modelItems.forEach(x => models.push(x.model as MetabaseTypes.ModelType));
      }
    }

    const [response, userAssignedResult] = await Promise.all([
      apiGet(`collection/${collectionId}/items`, models.length > 0 ? { models } : undefined),
      !!options && !!options.currentUser ? client.query({ query: ALL_USER_ASSIGNED_METABASE_DASHBOARDS }) : null,
    ]);
    const assigned = !!userAssignedResult
      ? (userAssignedResult.data as ALL_USER_ASSIGNED_METABASE_DASHBOARDSType).data.map(x => x.metabaseId)
      : null;

    var data = (response || empty) as MetabaseTypes.CollectionGetItemsType;
    if (!!options) {
      var filteredData: MetabaseTypes.CollectionGetItemsModelType[] = data.data;
      if (!!options.modelItems) filteredData = apiCollectionItemsFilter(data.data, options.modelItems);
      if (!!assigned) filteredData = filteredData.filter(x => assigned.includes(x.id));
      data = {
        ...data,
        total: filteredData.length,
        data: filteredData,
      };
    }
    return data;
  };

  // Public functions
  const getMetabaseUrl = (path?: string): string => {
    if (isMetabaseDisabled()) {
      return '';
    }
    if (!path) return METABASE_URL;
    return `${METABASE_URL}/${path.replace(/^\/+/, '')}`;
  };

  /** Force the embed UI modifiers onto the return_to target, overriding any conflicting values */
  const applyEmbedMods = (returnTo: string): string => {
    try {
      const base = new URL(`${METABASE_URL}/`);
      const target = new URL(returnTo || '/', base);
      // Metabase JWT SSO only accepts a same-origin path
      if (target.origin !== base.origin) return applyEmbedMods('/');
      Object.keys(METABASE_EMBED_MODS).forEach(key => target.searchParams.set(key, METABASE_EMBED_MODS[key]));
      // return_to must stay on the Metabase host; pass a relative path
      return `${target.pathname}${target.search}${target.hash}`;
    } catch (e) {
      const mods = Object.keys(METABASE_EMBED_MODS)
        .map(key => `${key}=${METABASE_EMBED_MODS[key]}`)
        .join('&');
      return `${returnTo}${returnTo.indexOf('?') >= 0 ? '&' : '?'}${mods}`;
    }
  };

  const getAuthUrl = async (returnTo: string, token?: string): Promise<string> => {
    if (isMetabaseDisabled()) {
      return '';
    }
    const jwtoken = token || (await getLoginToken());
    if (!jwtoken) return '';
    return `${METABASE_URL}/auth/sso?jwt=${encodeURIComponent(jwtoken)}&return_to=${encodeURIComponent(
      applyEmbedMods(returnTo)
    )}`;
  };

  const getLoginToken = async (): Promise<string> => {
    if (isMetabaseDisabled() || !canUseMetabase()) {
      return '';
    }
    const result: any = await client.query({
      query: METABASE_LOGIN_TOKEN,
      fetchPolicy: 'no-cache',
    });
    const token = result && result.data && result.data.getMetabaseLoginToken && result.data.getMetabaseLoginToken.token;
    if (!token) console.error('Metabase', 'Login token missing from getMetabaseLoginToken response.');
    return token || '';
  };

  const getItemUrl = (model: MetabaseTypes.ModelType, id: number): string => {
    if (isMetabaseDisabled()) {
      return '';
    }
    if (model === 'card') return `/question/${id}`;
    if (model === 'dataset') return `/model/${id}`;
    if (model === 'dashboard') return `/dashboard/${id}`;
    return `${model}/${id}`;
  };

  /** Notebook editor for a new question; the save dialog suggests the given collection (IB-1886) */
  const getNewQuestionPath = (collectionId?: number): string => {
    const card: any = {
      dataset_query: { database: null, type: 'query', query: { 'source-table': null } },
      display: 'table',
      parameters: [],
      visualization_settings: {},
      type: 'question',
    };
    if (collectionId !== undefined && !isNaN(collectionId)) card.collection_id = collectionId;
    return `question/notebook#${window.btoa(JSON.stringify(card))}`;
  };

  /** Organisation admins/owners can create questions and dashboards (server enforces the same rule) */
  const isOrganisationAdmin = (): boolean => {
    const userOrganisation: any = getAuthStateFromToken().user.userOrganisation;
    const role = userOrganisation && userOrganisation.role;
    const name = typeof role === 'string' ? role : (role && role.name) || '';
    return /(ADMIN|OWNER)$/.test(name);
  };

  const getUrl = (options: {
    model?: MetabaseTypes.ModelType;
    id?: number;
    ui?:
      | {
          top_nav?: boolean;
          side_nav?: boolean;
          search?: boolean;
          new_button?: boolean;
          header?: boolean;
          additional_info?: boolean;
          action_buttons?: boolean;
        }
      | boolean;
  }): string => {
    if (isMetabaseDisabled()) {
      return '';
    }
    var path = !!options && !!options.model ? `${options.model}/${options.id}` : '';
    if (!!options && !!options.ui) {
      const params = new URLSearchParams();
      if (typeof options.ui === 'boolean') {
        // logo, top_nav and side_nav are always forced off by getAuthUrl (METABASE_EMBED_MODS)
        params.append('search', 'true');
        params.append('new_button', 'true');
        params.append('header', 'true');
        params.append('additional_info', 'true');
        params.append('action_buttons', 'true');
      } else {
        for (var key in options.ui) {
          const val = (options.ui as any)[key];
          if (val !== undefined) {
            params.append(key, val.toString());
          }
        }
      }
      const search = params.toString();
      if (!!search) path += '?' + search;
    }
    return path;
  };

  /** Health check before showing Metabase pages: the server can sign in to Metabase as this organisation */
  const prepare = async (): Promise<boolean> => {
    if (isMetabaseDisabled() || !canUseMetabase()) return true;
    try {
      await apiGet('collection');
      return true;
    } catch (e) {
      console.error('Metabase', 'Unable to reach Metabase.', e);
      return false;
    }
  };

  // Kept for callers; there is no browser-side Metabase session any more.
  const connect = async () => {};
  const disconnect = async () => {};

  return {
    getAuthUrl,
    getLoginToken,
    getMetabaseUrl,
    getItemUrl,
    getNewQuestionPath,
    getUrl,
    isOrganisationAdmin,

    api: {
      connect,
      disconnect,
      prepare,
      collection: {
        all: apiCollectionAll,
        get: apiCollectionGet,
        getItems: apiCollectionGetItems,
      },
    },

    utils: {
      applyEmbedMods,
      collectionItemsFilter: apiCollectionItemsFilter,
      collectionItemsFilterItem: apiCollectionItemsFilterItem,
      isMetabaseDisabled: isMetabaseDisabled,
    },
  };
};
